import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import BuscadorVentas from '../components/BuscadorVentas';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  RotateCcw,
  User,
  Receipt,
  Plus,
  Minus,
  Trash2,
  Check,
  CreditCard,
  AlertTriangle,
  Loader2,
  MapPin,
  Calendar,
  Package,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { restoreVariantStock, splitVariantItems, fetchCambiosTalle, redistribuirPorCambiosTalle } from '../utils/variantStock';
import { devolverVentaALotes } from '../utils/stockLotes';
import CambioTalleModal from '../components/ventas/CambioTalleModal';
import { useSite } from '../context/SiteContext';
import { buildRestorableQuantityMap } from '../utils/comboStock';
import {
  construirLineasDevolucion,
  calcularTotalDevolucion,
  expandirItemsParaStock,
} from '../utils/comboDevolucion';
import { applyActiveProductsFilter } from '../utils/productStatus';

const currency = (n) =>
  Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayInput = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// Valor del selector de reintegro para una venta administrativa cuando la
// organizacion no tiene un metodo "cuenta corriente": fuerza el circuito de
// nota de credito sin inventar un payment_method_id.
const CTA_CTE_SIN_METODO = '__cuenta_corriente__';

// Venta con cliente con nombre (no el generico "Consumidor Final"): la unica
// que puede reintegrarse por nota de credito a cuenta corriente.
const ventaConCuentaCorriente = (venta) =>
  Boolean(venta?.client_id)
  && String(venta?.clients?.full_name || '').trim().toLowerCase() !== 'consumidor final';

function DevolucionNueva() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { selectedSiteId } = useSite();
  const [searchParams] = useSearchParams();
  const preselectSaleId = searchParams.get('saleId') || '';

  const [step, setStep] = useState(preselectSaleId ? 'items' : 'buscar'); // 'buscar' | 'items'
  // Talles y colores: cambio de talle/color al mismo precio (sin dinero).
  const [cambioTalleItem, setCambioTalleItem] = useState(null);
  const [returnMode, setReturnMode] = useState('sale'); // 'sale' | 'manual'
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [saving, setSaving] = useState(false);

  // Búsqueda de venta

  // Venta seleccionada
  const [sale, setSale] = useState(null);
  const [saleItems, setSaleItems] = useState([]);
  const [prevReturnedQtyMap, setPrevReturnedQtyMap] = useState({}); // sale_item_id -> qty ya devuelta

  // Selección de items a devolver (sale_item_id -> qty)
  const [selectedQty, setSelectedQty] = useState({});

  // Método de pago para el reintegro
  const [metodosPago, setMetodosPago] = useState([]);
  const [productos, setProductos] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [metodoPagoId, setMetodoPagoId] = useState('');
  // Venta administrativa (sales.afecta_stock = false): la mercaderia no se
  // desconto al vender, asi que por defecto tampoco se reingresa.
  const [reintegrarStock, setReintegrarStock] = useState(true);
  const [fechaDevolucion, setFechaDevolucion] = useState(todayInput());
  const [reason, setReason] = useState('');
  const [observations, setObservations] = useState('');
  const [manualSiteId, setManualSiteId] = useState(selectedSiteId || '');
  const [manualClientId, setManualClientId] = useState('');
  const [manualProductSearch, setManualProductSearch] = useState('');
  const [manualProductId, setManualProductId] = useState('');
  const [manualQty, setManualQty] = useState('1');
  const [manualUnitPrice, setManualUnitPrice] = useState('');
  const [manualItems, setManualItems] = useState([]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    // Cargar métodos de pago activos
    supabase
      .from('payment_methods')
      .select('id, name, bank_id, accreditation_days, accreditation_business_days, commission_percentage, system_code')
      .eq('organization_id', profile.organization_id)
      .eq('is_active', true)
      .order('name')
      .then(({ data, error }) => {
        if (error) {
          console.error(error);
          return;
        }
        setMetodosPago(data || []);
      });
  }, [profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    cargarMaestros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, selectedSiteId]);

  useEffect(() => {
    if (selectedSiteId && !manualSiteId) setManualSiteId(selectedSiteId);
  }, [manualSiteId, selectedSiteId]);

  async function cargarMaestros() {
    try {
      const orgId = profile.organization_id;
      const [productosRes, sitiosRes, clientesRes] = await Promise.all([
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, barcode, brand, sell_price, product_variants(price), stock_levels(*)')
          .eq('organization_id', orgId)
        )
          .order('name', { ascending: true }),
        supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).order('nombre'),
        supabase.from('clients').select('id, full_name').eq('organization_id', orgId).order('full_name'),
      ]);

      if (productosRes.error) throw productosRes.error;
      if (sitiosRes.error) throw sitiosRes.error;
      if (clientesRes.error) throw clientesRes.error;

      setProductos(productosRes.data || []);
      setSitios(sitiosRes.data || []);
      setClientes(clientesRes.data || []);
      setManualSiteId((prev) => prev || selectedSiteId || sitiosRes.data?.[0]?.id || '');
    } catch (error) {
      console.error(error);
      toast.error('Error cargando datos de devoluciones');
    }
  }

  // Si vinimos con ?saleId=, cargar directo
  useEffect(() => {
    if (preselectSaleId && profile?.organization_id) {
      cargarVenta(preselectSaleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectSaleId, profile?.organization_id]);

  async function cargarVenta(saleId) {
    try {
      setLoadingSearch(true);
      const orgId = profile.organization_id;

      const [saleRes, itemsRes, prevReturnsRes] = await Promise.all([
        supabase
          .from('sales')
          .select('*, clients(*), sitios(*)')
          .eq('id', saleId)
          .eq('organization_id', orgId)
          .single(),
        supabase
          .from('sale_items')
          // combo_role y parent_combo_product_id son OBLIGATORIAS: sin ellas
          // construirLineasDevolucion no puede emparejar los componentes con su
          // combo, los trata como legacy y no reintegra nada de stock. Hay un
          // test estático que ata este select a esas dos columnas.
          .select('id, product_id, variant_id, product_name_snapshot, quantity, unit_price, subtotal, combo_role, parent_combo_product_id, products!vinc_prod(name, stock_tracking)')
          .eq('sale_id', saleId)
          .eq('organization_id', orgId),
        supabase
          .from('return_items')
          .select('sale_item_id, quantity, returns!inner(active, organization_id)')
          .eq('organization_id', orgId)
          .eq('returns.active', true),
      ]);

      if (saleRes.error) throw saleRes.error;
      if (itemsRes.error) throw itemsRes.error;

      if (!saleRes.data.active) {
        toast.error('Esta venta está anulada');
        return;
      }

      setSale(saleRes.data);
      setSaleItems(itemsRes.data || []);

      // Acumular cantidades ya devueltas por sale_item_id
      const map = {};
      (prevReturnsRes.data || []).forEach((ri) => {
        if (!ri.sale_item_id) return;
        map[ri.sale_item_id] = (map[ri.sale_item_id] || 0) + Number(ri.quantity || 0);
      });
      setPrevReturnedQtyMap(map);

      // Inicializar selección en 0. Solo las líneas devolvibles: cargar claves
      // de filas `component` dejaría en el estado ítems que nadie puede elegir,
      // listos para que un consumidor futuro los sume por error.
      const initSel = {};
      construirLineasDevolucion(itemsRes.data || []).forEach((linea) => {
        initSel[linea.id] = 0;
      });
      setSelectedQty(initSel);

      setReintegrarStock(saleRes.data.afecta_stock !== false);

      // Sugerir método de pago original. Una venta administrativa sin método
      // de cuenta corriente configurado va igual a cuenta corriente (NC).
      if (saleRes.data.payment_method_id) {
        setMetodoPagoId(saleRes.data.payment_method_id);
      } else if (saleRes.data.afecta_caja === false && ventaConCuentaCorriente(saleRes.data)) {
        setMetodoPagoId(CTA_CTE_SIN_METODO);
      }

      setStep('items');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cargar la venta');
    } finally {
      setLoadingSearch(false);
    }
  }

  // Líneas devolvibles: producto suelto o combo (parent), con sus componentes
  // colgados como detalle informativo. Las filas `component` nunca son líneas
  // propias — mostrarlas o sumarlas por separado es lo que producía el doble
  // cobro y el reintegro de stock fantasma.
  const lineasDevolucion = useMemo(() => construirLineasDevolucion(saleItems), [saleItems]);

  const totalVentaDevolucion = useMemo(
    () => calcularTotalDevolucion({ saleItems, cantidadPorItemId: selectedQty }),
    [saleItems, selectedQty]
  );

  const totalManualDevolucion = useMemo(
    () => manualItems.reduce((acc, it) => acc + Number(it.subtotal || 0), 0),
    [manualItems]
  );

  const totalDevolucion = returnMode === 'manual' ? totalManualDevolucion : totalVentaDevolucion;

  const itemsSeleccionados = useMemo(
    () => (
      returnMode === 'manual'
        ? manualItems
        : lineasDevolucion.filter((it) => Number(selectedQty[it.id] || 0) > 0)
    ),
    [manualItems, returnMode, lineasDevolucion, selectedQty]
  );

  const selectedManualProduct = useMemo(
    () => productos.find((product) => product.id === manualProductId) || null,
    [manualProductId, productos]
  );

  const productosFiltradosManual = useMemo(() => {
    const q = manualProductSearch.trim().toLowerCase();
    const list = !q
      ? productos
      : productos.filter((product) => {
          const name = (product.name || '').toLowerCase();
          const barcode = (product.barcode || '').toLowerCase();
          const brand = (product.brand || '').toLowerCase();
          return name.includes(q) || barcode.includes(q) || brand.includes(q);
        });
    return list.slice(0, 30);
  }, [manualProductSearch, productos]);

  const setQty = (itemId, qty, max) => {
    const v = Math.max(0, Math.min(Number(qty) || 0, max));
    setSelectedQty((prev) => ({ ...prev, [itemId]: v }));
  };

  const seleccionarTodo = () => {
    const sel = {};
    lineasDevolucion.forEach((it) => {
      const max = Number(it.quantity) - (prevReturnedQtyMap[it.id] || 0);
      sel[it.id] = Math.max(0, max);
    });
    setSelectedQty(sel);
  };

  const limpiarSeleccion = () => {
    const sel = {};
    lineasDevolucion.forEach((it) => {
      sel[it.id] = 0;
    });
    setSelectedQty(sel);
  };

  const getManualProductPrice = (product) =>
    Number(product?.sell_price ?? product?.product_variants?.[0]?.price ?? 0) || 0;

  const selectManualProduct = (product) => {
    setManualProductId(product.id);
    setManualProductSearch(product.name || '');
    setManualUnitPrice(String(getManualProductPrice(product)));
  };

  const agregarManualItem = () => {
    if (!selectedManualProduct) {
      toast.error('Elegi un producto');
      return;
    }
    const qty = Number(String(manualQty).replace(',', '.')) || 0;
    const unit = Number(String(manualUnitPrice).replace(',', '.')) || 0;
    if (qty <= 0) {
      toast.error('La cantidad debe ser mayor a cero');
      return;
    }
    if (unit < 0) {
      toast.error('El importe no puede ser negativo');
      return;
    }

    const item = {
      id: `manual-${selectedManualProduct.id}`,
      product_id: selectedManualProduct.id,
      variant_id: null,
      product_name_snapshot: selectedManualProduct.name || 'Producto',
      quantity: qty,
      unit_price: unit,
      subtotal: qty * unit,
    };

    setManualItems((prev) => {
      const idx = prev.findIndex((it) => it.product_id === item.product_id);
      if (idx === -1) return [...prev, item];
      return prev.map((it, i) => (i === idx ? item : it));
    });
    setManualProductId('');
    setManualProductSearch('');
    setManualQty('1');
    setManualUnitPrice('');
  };

  const quitarManualItem = (productId) => {
    setManualItems((prev) => prev.filter((it) => it.product_id !== productId));
  };

  async function procesarDevolucion() {
    if (returnMode === 'sale' && !sale) return;
    if (returnMode === 'manual' && !manualSiteId) {
      toast.error('Elegi la sucursal donde vuelve el stock');
      return;
    }
    if (itemsSeleccionados.length === 0) {
      toast.error('Seleccioná al menos un item');
      return;
    }
    if (!metodoPagoId) {
      toast.error('Elegí el método de reintegro');
      return;
    }

    try {
      setSaving(true);
      const orgId = profile.organization_id;
      const authUser = (await supabase.auth.getUser()).data?.user;
      const metodo = metodosPago.find((m) => m.id === metodoPagoId);
      const metodoPagoIdReal = metodoPagoId === CTA_CTE_SIN_METODO ? null : metodoPagoId;
      const sitioId = returnMode === 'manual' ? manualSiteId : sale.site_id;
      const clientId = returnMode === 'manual' ? (manualClientId || null) : sale.client_id;
      const clienteNombre =
        returnMode === 'manual'
          ? (clientes.find((c) => c.id === manualClientId)?.full_name || 'Consumidor Final')
          : (sale.clients?.full_name || 'Consumidor Final');
      const origenLabel = returnMode === 'manual' ? 'sin factura asociada' : `venta #${String(sale.id).slice(0, 6)}`;

      // 0) Resolver QUÉ se va a reintegrar, ANTES de escribir nada.
      // La fila padre de un combo NO reintegra stock: en la venta se descontó el
      // stock de sus componentes, nunca el del producto combo, así que
      // reintegrarlo crearía stock fantasma. Mismo criterio que
      // utils/saleAnnulment.js, vía utils/comboStock.js.
      //
      // Esta lectura va primero a propósito: la función no es transaccional, y
      // si la query de productos falla después de insertar la cabecera y los
      // items quedaría una devolución registrada sin reintegro de stock ni
      // movimiento financiero — peor que no registrarla.
      // En modo venta, expandimos los combos seleccionados a sus componentes
      // (prorrateados por la fracción de combos devuelta); un producto suelto
      // se reintegra tal cual. Los ítems ya expandidos no llevan combo_role,
      // así que buildRestorableQuantityMap los agrega sin filtrar de más;
      // productTypeById sigue siendo necesario para el combo legacy SIN
      // combo_role (una sola fila, detectable solo por product_type='Combo') y
      // para el modo manual. El legacy con combo_role='parent' y sin
      // componentes ni llega hasta acá: la expansión ya lo descarta.
      //
      // La expansión no toca la red, así que va antes del fetch de productos:
      // los product_type se piden sobre lo que REALMENTE se va a reintegrar
      // (componentes incluidos), no sobre las líneas elegidas. Así un combo
      // anidado —un componente que a su vez es Combo— también queda filtrado.
      const itemsParaStock =
        returnMode === 'manual'
          ? itemsSeleccionados.map((it) => ({
              product_id: it.product_id,
              quantity: Number(it.quantity || 0),
            }))
          : expandirItemsParaStock({ saleItems, cantidadPorItemId: selectedQty });

      const productIdsInvolucrados = Array.from(
        new Set(itemsParaStock.map((it) => it.product_id).filter(Boolean))
      );

      let productTypeById = new Map();
      let variantTrackedIds = new Set();
      // Vencimientos y lotes: vuelven por la base a los lotes de los que salió la venta.
      let lotTrackedIds = new Set();
      if (productIdsInvolucrados.length > 0) {
        const { data: productosInvolucrados, error: productosErr } = await supabase
          .from('products')
          .select('id, product_type, stock_tracking, controla_vencimiento')
          .in('id', productIdsInvolucrados);

        if (productosErr) throw productosErr;

        productTypeById = new Map(
          (productosInvolucrados || []).map((p) => [p.id, p.product_type])
        );
        variantTrackedIds = new Set(
          (productosInvolucrados || []).filter((p) => p.stock_tracking === 'variant').map((p) => p.id)
        );
        lotTrackedIds = new Set(
          (productosInvolucrados || [])
            .filter((p) => p.controla_vencimiento && p.stock_tracking !== 'variant')
            .map((p) => p.id)
        );
      }

      // Talles y colores: los productos con stock por talle se reponen al talle
      // devuelto (la línea de venta conserva variant_id). En devolución manual no
      // hay talle de origen: va a "Sin clasificar" y se reubica desde la grilla.
      const lineasConTalle = returnMode === 'manual'
        ? itemsSeleccionados.map((it) => ({
            product_id: it.product_id,
            variant_id: it.variant_id ?? null,
            quantity: Number(it.quantity || 0),
          }))
        : construirLineasDevolucion(saleItems)
            .filter((linea) => String(linea.combo_role || '') !== 'parent')
            .map((linea) => ({
              sale_item_id: linea.id,
              product_id: linea.product_id,
              variant_id: linea.variant_id ?? null,
              quantity: Number(selectedQty[linea.id] || 0),
            }));
      // Cambios de talle previos: lo que vuelve es el talle que se llevó el cliente.
      let lineasReposicionTalle = lineasConTalle;
      const idsConTalle = lineasConTalle
        .filter((linea) => linea.sale_item_id && variantTrackedIds.has(linea.product_id) && linea.quantity > 0)
        .map((linea) => linea.sale_item_id);
      if (returnMode !== 'manual' && idsConTalle.length > 0) {
        const cambios = await fetchCambiosTalle(supabase, idsConTalle);
        if (cambios.length > 0) {
          lineasReposicionTalle = redistribuirPorCambiosTalle(lineasConTalle, cambios, { devueltoPorItem: prevReturnedQtyMap });
        }
      }
      const { variantItems: itemsConTalle } = splitVariantItems({ items: lineasReposicionTalle, variantTrackedIds });

      const cantidadPorProducto = buildRestorableQuantityMap({
        items: itemsParaStock.filter((it) => !variantTrackedIds.has(it.product_id)),
        productTypeById,
      });

      // 1) Insertar cabecera de devolución
      const { data: ret, error: retErr } = await supabase
        .from('returns')
        .insert({
          organization_id: orgId,
          sale_id: returnMode === 'manual' ? null : sale.id,
          client_id: clientId,
          site_id: sitioId,
          user_id: authUser?.id || null,
          total: totalDevolucion,
          payment_method: metodo?.name || null,
          payment_method_id: metodoPagoIdReal,
          reason: reason || null,
          observations: observations || null,
          status: 'completed',
          active: true,
          fecha_devolucion: fechaDevolucion,
        })
        .select('id')
        .single();

      if (retErr) throw retErr;
      const returnId = ret.id;

      // 2) Insertar items
      const returnItemsPayload = itemsSeleccionados.map((it) => {
        const qty = returnMode === 'manual' ? Number(it.quantity || 0) : Number(selectedQty[it.id] || 0);
        const unit = Number(it.unit_price || 0);
        return {
          return_id: returnId,
          organization_id: orgId,
          sale_item_id: returnMode === 'manual' ? null : it.id,
          product_id: it.product_id,
          variant_id: it.variant_id,
          product_name_snapshot: it.product_name_snapshot || it.products?.name || null,
          quantity: qty,
          unit_price: unit,
          subtotal: qty * unit,
        };
      });

      const { error: itemsErr } = await supabase.from('return_items').insert(returnItemsPayload);
      if (itemsErr) throw itemsErr;

      // 3) Reintegrar stock en la sucursal elegida (ya filtrado en el paso 0:
      // un movimiento de kardex por producto, no por línea).
      const debeReintegrarStock = returnMode === 'manual' || reintegrarStock;
      for (const [productId, qty] of (debeReintegrarStock ? cantidadPorProducto : new Map()).entries()) {
        if (lotTrackedIds.has(productId)) {
          // Devolución manual (sin venta): entra a "sin lote". Un error acá no
          // corta la devolución (los ítems ya están grabados): se avisa para
          // corregir el stock a mano, igual que el camino de siempre.
          try {
            await devolverVentaALotes({
              supabase,
              organizationId: orgId,
              productId,
              siteId: sitioId,
              cantidad: qty,
              saleId: returnMode === 'manual' ? null : sale?.id,
              motivo: `Devolucion cliente (${origenLabel}).`,
              refTipo: 'devolucion_venta',
            });
          } catch (loteError) {
            console.error('Devolución: no se pudo reintegrar el stock por lotes', loteError);
            toast.error(`No se pudo reintegrar el stock de un producto con vencimiento: ${loteError.message}. Ajustalo a mano.`);
          }
          continue;
        }
        const { data: st } = await supabase
          .from('stock_levels')
          .select('id, quantity')
          .eq('product_id', productId)
          .eq('site_id', sitioId)
          .maybeSingle();

        if (st?.id) {
          await supabase
            .from('stock_levels')
            .update({
              quantity: Number(st.quantity || 0) + qty,
              updated_at: new Date().toISOString(),
            })
            .eq('id', st.id);
        } else {
          await supabase.from('stock_levels').insert([
            {
              organization_id: orgId,
              product_id: productId,
              site_id: sitioId,
              quantity: qty,
              min_stock: 0,
            },
          ]);
        }

        // Log movimiento de stock (Ingreso, motivo Devolución)
        await supabase.from('movimientos_stock').insert([
          {
            organization_id: orgId,
            producto_id: productId,
            tipo: 'Ingreso',
            cantidad: qty,
            motivo: `Devolucion cliente (${origenLabel}).`,
            sitio_origen: null,
            sitio_destino: sitioId,
          },
        ]);
      }

      if (debeReintegrarStock && itemsConTalle.length > 0) {
        await restoreVariantStock({
          supabase,
          organizationId: orgId,
          siteId: sitioId,
          items: itemsConTalle,
          motivo: `Devolucion cliente (${origenLabel}).`,
        });
      }

      // ¿El reintegro es por cuenta corriente (nota de crédito)? Se decide acá
      // arriba porque condiciona los pasos 4 y 5: una NC NO mueve plata, así
      // que no debe registrar Egreso en libro diario, banco ni caja (antes lo
      // hacía, inflando los egresos del Flujo de Fondos con plata que nunca
      // salió).
      const isCtaCte =
        metodoPagoId === CTA_CTE_SIN_METODO ||
        String(metodo?.system_code || '').toLowerCase().includes('cuenta_corriente') ||
        String(metodo?.name || '').toLowerCase().includes('cuenta corriente');

      // 4) Movimiento financiero (Egreso categoría Devolución) — solo si hay
      // salida real de dinero (efectivo/banco), nunca para nota de crédito.
      if (!isCtaCte) {
        const descMov = `Devolucion ${origenLabel} (${metodo?.name || 'Pago'}). Cliente: ${clienteNombre}.`;

        // Si el método tiene bank_id y acreditación inmediata, descontar del banco
        let saldoResultante = null;
        const accreditationDays = Math.max(0, Number(metodo?.accreditation_days) || 0);
        const settlesImmediately = Boolean(metodo?.bank_id) && accreditationDays === 0;

        if (settlesImmediately) {
          const { data: banco } = await supabase
            .from('banks')
            .select('id, current_balance')
            .eq('id', metodo.bank_id)
            .single();

          if (banco) {
            saldoResultante = Number(banco.current_balance || 0) - totalDevolucion;
            await supabase.from('banks').update({ current_balance: saldoResultante }).eq('id', banco.id);
          }
        }

        await supabase.from('movimientos_financieros').insert([
          {
            organization_id: orgId,
            bank_id: metodo?.bank_id || null,
            sitio_id: sitioId,
            tipo: 'Egreso',
            categoria: 'Devolucion',
            monto: totalDevolucion,
            saldo_resultante: saldoResultante,
            entidad_tipo: 'Cliente',
            entidad_id: clientId,
            documento_tipo: 'Devolucion',
            documento_id: returnId,
            descripcion: descMov,
            fecha_operacion: new Date().toISOString(),
            user_id: authUser?.id || null,
          },
        ]);

        // 5) Impacto en turno de caja abierto (si hay)
        const { data: turnoAbierto } = await supabase
          .from('turnos_caja')
          .select('id')
          .eq('organization_id', orgId)
          .eq('estado', 'Abierto')
          .eq('sitio_id', sitioId)
          .maybeSingle();

        // Una venta administrativa nunca paso por la caja: su reintegro
        // tampoco sale del arqueo (se registra en banco/libro diario arriba).
        const ventaFueraDeCaja = returnMode === 'sale' && sale?.afecta_caja === false;
        if (turnoAbierto?.id && !ventaFueraDeCaja) {
          await supabase.from('movimientos_caja').insert({
            turno_id: turnoAbierto.id,
            tipo: 'Egreso',
            categoria: 'Devolucion',
            monto: totalDevolucion,
            descripcion: `Devolucion ${origenLabel} (${metodo?.name || 'Pago'})`,
            organization_id: orgId,
            payment_method_id: metodoPagoIdReal,
            sitio_id: sitioId,
            fecha: new Date().toISOString(),
          });
        }
      }

      // 6) Cuenta corriente del cliente — generar nota de crédito si el método es cuenta corriente
      if (isCtaCte && clientId) {
        // Movimiento "Credito" en cuenta corriente (resta saldo deudor del cliente)
        const { data: lastMov } = await supabase
          .from('client_movements')
          .select('current_balance')
          .eq('client_id', clientId)
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
          .limit(1);

        const prevBalance = Number(lastMov?.[0]?.current_balance || 0);
        const newBalance = prevBalance - totalDevolucion;

        await supabase.from('client_movements').insert({
          organization_id: orgId,
          client_id: clientId,
          type: 'Nota de Credito',
          observations: `Devolucion ${origenLabel}. ${reason || ''}`.trim(),
          amount: -totalDevolucion,
          current_balance: newBalance,
          sale_id: returnMode === 'manual' ? null : sale.id,
        });

        // La NC también debe reflejarse en CUENTAS POR COBRAR. Antes solo
        // bajaba el saldo de cta cte y el documento seguía abierto por el
        // total: aging, cobranzas sugeridas y flujo proyectado sobreestimaban.
        if (returnMode !== 'manual' && sale?.id) {
          // Devolución de una venta concreta: ese documento ahora vale menos.
          const { data: rcv } = await supabase
            .from('sales_receivables')
            .select('id, total_amount, pending_amount, settled_amount')
            .eq('sale_id', sale.id)
            .maybeSingle();

          if (rcv?.id) {
            const settled = Math.max(0, Number(rcv.settled_amount) || 0);
            const nextPending = Math.max(0, (Number(rcv.pending_amount) || 0) - totalDevolucion);
            const nextTotal = Math.max(settled, (Number(rcv.total_amount) || 0) - totalDevolucion);

            await supabase
              .from('sales_receivables')
              .update({
                total_amount: nextTotal,
                pending_amount: nextPending,
                is_active: nextPending > 0,
                updated_at: new Date().toISOString(),
              })
              .eq('id', rcv.id);
          }
        } else {
          // Devolución manual (sin venta asociada): aplicamos el crédito a los
          // documentos abiertos más viejos del cliente, como una cobranza.
          let creditoRestante = totalDevolucion;
          const { data: abiertos } = await supabase
            .from('sales_receivables')
            .select('id, pending_amount, settled_amount')
            .eq('organization_id', orgId)
            .eq('client_id', clientId)
            .eq('is_active', true)
            .gt('pending_amount', 0)
            .order('due_date', { ascending: true })
            .order('created_at', { ascending: true });

          for (const doc of abiertos || []) {
            if (creditoRestante <= 0) break;
            const pendiente = Math.max(0, Number(doc.pending_amount) || 0);
            const aplicado = Math.min(creditoRestante, pendiente);
            if (aplicado <= 0) continue;

            const nextPending = pendiente - aplicado;
            await supabase
              .from('sales_receivables')
              .update({
                pending_amount: nextPending,
                settled_amount: Math.max(0, Number(doc.settled_amount) || 0) + aplicado,
                is_active: nextPending > 0,
                updated_at: new Date().toISOString(),
              })
              .eq('id', doc.id);

            creditoRestante -= aplicado;
          }
        }
      }

      toast.success('Devolución registrada correctamente');
      navigate(`/sistema/devoluciones/${returnId}`);
    } catch (e) {
      console.error(e);
      toast.error('Error al procesar la devolución: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  }

  // ============ RENDER ============

  if (step === 'buscar') {
    return (
      <div className="min-h-screen bg-[#F2F9FF] p-4 md:p-8 font-sans text-slate-700">
        <div className="max-w-4xl mx-auto mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/sistema/devoluciones')}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none flex items-center gap-3">
              <RotateCcw size={22} className="text-[#51A7FD]" /> Nueva devolución
            </h1>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-2">
              Paso 1 — Buscar venta original
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
            <button
              type="button"
              onClick={() => setReturnMode('sale')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                returnMode === 'sale'
                  ? 'bg-[#0B2A36] text-white border-[#0B2A36]'
                  : 'bg-slate-50 text-slate-500 border-slate-100 hover:bg-slate-100'
              }`}
            >
              <Receipt size={14} /> Con venta/factura
            </button>
            <button
              type="button"
              onClick={() => {
                setReturnMode('manual');
                setStep('items');
              }}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-100 bg-slate-50 text-slate-500 hover:bg-slate-100 text-[10px] font-black uppercase tracking-widest transition-all"
            >
              <Package size={14} /> Sin factura
            </button>
          </div>

          {loadingSearch && (
            <div className="mb-4 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <Loader2 size={14} className="animate-spin" /> Cargando venta...
            </div>
          )}

          <BuscadorVentas
            organizationId={profile?.organization_id}
            selectColumns="id, created_at, fecha_venta, total, payment_method, payment_method_id, numero_factura, invoice_status, cae, active, site_id, client_id, clients(full_name), sitios(nombre)"
            onSelect={(v) => cargarVenta(v.id)}
            placeholder="Buscar por cliente, N° de factura o ID..."
            vacioLabel="Ninguna venta coincide. Ampliá el período o revisá los filtros."
          />
        </div>
      </div>
    );
  }

  // STEP: items
  return (
    <div className="min-h-screen bg-[#F2F9FF] p-4 md:p-8 font-sans text-slate-700">
      {cambioTalleItem && (
        <CambioTalleModal
          saleItem={cambioTalleItem}
          sites={sitios}
          defaultSiteId={sale?.site_id || selectedSiteId || ''}
          onClose={() => setCambioTalleItem(null)}
          onDone={() => {
            setCambioTalleItem(null);
            if (sale?.id) cargarVenta(sale.id);
          }}
        />
      )}
      {/* HEADER */}
      <div className="max-w-5xl mx-auto mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (preselectSaleId) navigate('/sistema/devoluciones');
              else setStep('buscar');
            }}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none flex items-center gap-3">
              <RotateCcw size={22} className="text-[#51A7FD]" /> Devolución
            </h1>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-2">
              Paso 2 — Elegí items y método de reintegro
            </p>
          </div>
        </div>
      </div>

      {returnMode === 'manual' ? (
        <div className="max-w-5xl mx-auto space-y-4">
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                  <MapPin size={11} /> Sucursal
                </label>
                <select
                  value={manualSiteId}
                  onChange={(e) => setManualSiteId(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold uppercase tracking-tight text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                >
                  <option value="">Seleccionar sucursal</option>
                  {sitios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                  <User size={11} /> Cliente (opcional)
                </label>
                <select
                  value={manualClientId}
                  onChange={(e) => setManualClientId(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold uppercase tracking-tight text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                >
                  <option value="">Consumidor Final</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                  <Calendar size={11} /> Fecha
                </label>
                <input
                  type="date"
                  value={fechaDevolucion}
                  onChange={(e) => setFechaDevolucion(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_110px_130px_auto] gap-3 items-end">
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                  <Package size={11} /> Producto
                </label>
                <input
                  type="text"
                  value={manualProductSearch}
                  onChange={(e) => {
                    setManualProductSearch(e.target.value);
                    setManualProductId('');
                  }}
                  placeholder="Buscar producto o codigo..."
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                />
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                  Cantidad
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualQty}
                  onChange={(e) => setManualQty(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                />
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                  Importe unit.
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualUnitPrice}
                  onChange={(e) => setManualUnitPrice(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                />
              </div>
              <button
                type="button"
                onClick={agregarManualItem}
                disabled={!selectedManualProduct}
                className="px-4 py-2.5 bg-[#51A7FD] text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-md hover:bg-[#3e91e8] disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <Plus size={14} /> Agregar
              </button>
            </div>

            {manualProductSearch && productosFiltradosManual.length > 0 && (
              <div className="mt-3 max-h-56 overflow-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
                {productosFiltradosManual.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => selectManualProduct(product)}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3 ${
                      manualProductId === product.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <span className="text-xs font-black uppercase text-slate-700 truncate">
                      {product.name}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 shrink-0">
                      $ {currency(getManualProductPrice(product))}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Productos a devolver sin factura
              </p>
            </div>
            {manualItems.length === 0 ? (
              <div className="p-8 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                Agrega al menos un producto
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50/50 text-[9px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="py-2 px-4 text-left">Producto</th>
                    <th className="py-2 px-4 text-center w-28">Cantidad</th>
                    <th className="py-2 px-4 text-right w-28">Precio</th>
                    <th className="py-2 px-4 text-right w-32">Subtotal</th>
                    <th className="py-2 px-4 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {manualItems.map((it) => (
                    <tr key={it.product_id}>
                      <td className="py-3 px-4 font-bold uppercase text-slate-700 tracking-tight">
                        {it.product_name_snapshot}
                      </td>
                      <td className="py-3 px-4 text-center font-bold text-slate-500">{it.quantity}</td>
                      <td className="py-3 px-4 text-right font-bold text-slate-600">$ {currency(it.unit_price)}</td>
                      <td className="py-3 px-4 text-right font-black text-slate-800">$ {currency(it.subtotal)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          onClick={() => quitarManualItem(it.product_id)}
                          className="p-1.5 text-slate-300 hover:text-rose-500"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                <CreditCard size={11} /> Metodo de reintegro
              </label>
              <select
                value={metodoPagoId}
                onChange={(e) => setMetodoPagoId(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold uppercase tracking-tight text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              >
                <option value="">Seleccionar</option>
                {metodosPago.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                Motivo (opcional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: cambio directo, producto defectuoso..."
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                Observaciones internas (opcional)
              </label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={2}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              />
            </div>
          </div>

          <div className="bg-[#1B252E] text-white rounded-2xl p-5 flex items-center justify-between">
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total a reintegrar</p>
              <p className="text-3xl font-black tracking-tighter">$ {currency(totalDevolucion)}</p>
            </div>
            <button
              onClick={procesarDevolucion}
              disabled={saving || manualItems.length === 0 || !manualSiteId || !metodoPagoId}
              className="flex items-center gap-2 px-6 py-3 bg-[#51A7FD] text-white rounded-xl font-black uppercase text-[11px] tracking-widest shadow-md hover:bg-[#3e91e8] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={3} />}
              Confirmar devolucion
            </button>
          </div>
        </div>
      ) : !sale ? (
        <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <Loader2 className="animate-spin mx-auto text-slate-300" />
        </div>
      ) : (
        <div className="max-w-5xl mx-auto space-y-4">
          {/* INFO VENTA */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Cliente</p>
              <p className="text-sm font-black uppercase text-slate-800 tracking-tight flex items-center gap-2">
                <User size={12} className="text-slate-300" />
                {sale.clients?.full_name || 'Consumidor Final'}
              </p>
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Venta</p>
              <p className="text-sm font-black uppercase text-slate-800 tracking-tight flex items-center gap-2">
                <Receipt size={12} className="text-slate-300" />
                {sale.numero_factura || `#${String(sale.id).slice(0, 8)}`}
              </p>
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Sucursal</p>
              <p className="text-sm font-black uppercase text-slate-800 tracking-tight flex items-center gap-2">
                <MapPin size={12} className="text-slate-300" />
                {sale.sitios?.nombre || '—'}
              </p>
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total venta</p>
              <p className="text-sm font-black text-slate-800">$ {currency(sale.total)}</p>
            </div>
          </div>

          {/* ITEMS */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Items de la venta</p>
              <div className="flex gap-2">
                <button
                  onClick={seleccionarTodo}
                  className="text-[10px] font-black uppercase tracking-widest text-[#51A7FD] hover:underline"
                >
                  Seleccionar todo
                </button>
                <span className="text-slate-200">|</span>
                <button
                  onClick={limpiarSeleccion}
                  className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:underline"
                >
                  Limpiar
                </button>
              </div>
            </div>

            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50/50 text-[9px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="py-2 px-4 text-left">Producto</th>
                  <th className="py-2 px-4 text-center w-28">Vendido</th>
                  <th className="py-2 px-4 text-center w-28">Ya devuelto</th>
                  <th className="py-2 px-4 text-center w-40">A devolver</th>
                  <th className="py-2 px-4 text-right w-28">Precio</th>
                  <th className="py-2 px-4 text-right w-32">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {lineasDevolucion.map((it) => {
                  const yaDevuelto = prevReturnedQtyMap[it.id] || 0;
                  const maxDev = Math.max(0, Number(it.quantity) - yaDevuelto);
                  const qty = Number(selectedQty[it.id] || 0);
                  const esCombo = String(it.combo_role || '') === 'parent';
                  return (
                    <React.Fragment key={it.id}>
                      <tr className={maxDev === 0 ? 'opacity-40' : ''}>
                        <td className="py-3 px-4 font-bold uppercase text-slate-700 tracking-tight">
                          {it.product_name_snapshot || it.products?.name || 'Producto'}
                          {it.variant_id && it.products?.stock_tracking === 'variant' && maxDev > 0 && (
                            <button
                              type="button"
                              onClick={() => setCambioTalleItem(it)}
                              className="ml-2 align-middle px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100 text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100"
                            >
                              Cambiar talle
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-slate-500">{it.quantity}</td>
                        <td className="py-3 px-4 text-center font-bold text-slate-400">{yaDevuelto || '—'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setQty(it.id, qty - 1, maxDev)}
                              disabled={qty <= 0 || maxDev === 0}
                              className="p-1.5 bg-slate-100 rounded-md text-slate-500 disabled:opacity-30 hover:bg-slate-200"
                            >
                              <Minus size={12} />
                            </button>
                            <input
                              type="number"
                              min="0"
                              max={maxDev}
                              value={qty}
                              onChange={(e) => setQty(it.id, e.target.value, maxDev)}
                              disabled={maxDev === 0}
                              className="w-14 text-center px-1 py-1 bg-slate-50 border border-slate-100 rounded-md text-xs font-black text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
                            />
                            <button
                              onClick={() => setQty(it.id, qty + 1, maxDev)}
                              disabled={qty >= maxDev || maxDev === 0}
                              className="p-1.5 bg-slate-100 rounded-md text-slate-500 disabled:opacity-30 hover:bg-slate-200"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-slate-600">$ {currency(it.unit_price)}</td>
                        <td className="py-3 px-4 text-right font-black text-slate-800">
                          $ {currency(qty * Number(it.unit_price || 0))}
                        </td>
                      </tr>
                      {esCombo && it.componentes.length > 0 && (
                        <tr className={maxDev === 0 ? 'opacity-40' : ''}>
                          <td colSpan={6} className="pb-3 px-4 -mt-2">
                            <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 italic pl-2">
                              Incluye: {it.componentes
                                .map(
                                  (c) =>
                                    `${c.quantity} × ${c.product_name_snapshot || c.products?.name || 'Producto'}`
                                )
                                .join(' · ')}
                            </p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* METODO DE PAGO + DATOS */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                <CreditCard size={11} /> Método de reintegro
              </label>
              <select
                value={metodoPagoId}
                onChange={(e) => setMetodoPagoId(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold uppercase tracking-tight text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              >
                <option value="">— Seleccionar —</option>
                {sale.afecta_caja === false && ventaConCuentaCorriente(sale) && (
                  <option value={CTA_CTE_SIN_METODO}>Cuenta corriente (nota de crédito)</option>
                )}
                {metodosPago.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.id === sale.payment_method_id ? '  (original)' : ''}
                  </option>
                ))}
              </select>
              {sale.afecta_caja === false && (
                <p className="text-[9px] font-bold text-violet-600 mt-1">
                  Venta administrativa: no pasó por caja ({sale.payment_method || "cuenta corriente"}). El reintegro tampoco toca el arqueo.
                </p>
              )}
              {sale.afecta_stock === false && (
                <label className="flex items-start gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={reintegrarStock}
                    onChange={(e) => setReintegrarStock(e.target.checked)}
                  />
                  <span className="text-[10px] font-bold text-slate-600">
                    Reingresar stock
                    <span className="block font-medium text-slate-400">La venta original no descontó stock. Tildalo solo si la mercadería vuelve físicamente.</span>
                  </span>
                </label>
              )}
              {sale.payment_method_id && (
                <p className="text-[9px] font-bold text-slate-400 italic mt-1">
                  Sugerido: el mismo método de la venta original.
                </p>
              )}
            </div>

            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                <Calendar size={11} /> Fecha de devolución
              </label>
              <input
                type="date"
                value={fechaDevolucion}
                onChange={(e) => setFechaDevolucion(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                Motivo (opcional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: producto defectuoso, error de talle..."
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                Observaciones internas (opcional)
              </label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={2}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#51A7FD]/30"
              />
            </div>
          </div>

          {/* AVISO */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold uppercase tracking-tight text-amber-700 leading-relaxed">
              Esta acción reintegra el stock al sitio de la venta y registra el reintegro en caja / banco / cuenta corriente
              según el método elegido.
            </p>
          </div>

          {/* FOOTER TOTAL + CTA */}
          <div className="bg-[#1B252E] text-white rounded-2xl p-5 flex items-center justify-between">
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total a reintegrar</p>
              <p className="text-3xl font-black tracking-tighter">$ {currency(totalDevolucion)}</p>
            </div>
            <button
              onClick={procesarDevolucion}
              disabled={saving || itemsSeleccionados.length === 0 || !metodoPagoId}
              className="flex items-center gap-2 px-6 py-3 bg-[#51A7FD] text-white rounded-xl font-black uppercase text-[11px] tracking-widest shadow-md hover:bg-[#3e91e8] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Procesando...
                </>
              ) : (
                <>
                  <Check size={14} strokeWidth={3} /> Confirmar devolución
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DevolucionNueva;
